// src/components/MonitoringDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Monitor as MonitorIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon
} from '@mui/icons-material';
import productionLogger from '../utils/productionLogger';
import performanceOptimizer from '../utils/performanceOptimizer';
import optimizedApiClient from '../utils/optimizedApiClient';

const MonitoringDashboard = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    errors: true,
    performance: true,
    security: false
  });
  const [loading, setLoading] = useState(false);

  const fetchMonitoringData = useCallback(async () => {
    setLoading(true);
    try {
      // Get frontend metrics
      const frontendMetrics = productionLogger.getMetrics();
      const performanceReport = performanceOptimizer.getPerformanceReport();
      const cacheStats = optimizedApiClient.getCacheStats();

      setMetrics({
        frontend: frontendMetrics,
        performance: performanceReport,
        cache: cacheStats
      });

      // Simulate fetching backend data (in production, these would be real API calls)
      if (process.env.NODE_ENV === 'development') {
        // Mock data for development
        setLogs([
          {
            timestamp: new Date().toISOString(),
            level: 'ERROR',
            message: 'Token refresh failed',
            category: 'authentication',
            severity: 'medium'
          },
          {
            timestamp: new Date(Date.now() - 300000).toISOString(),
            level: 'WARN',
            message: 'High cache miss rate detected',
            category: 'performance',
            severity: 'low'
          },
          {
            timestamp: new Date(Date.now() - 600000).toISOString(),
            level: 'INFO',
            message: 'Subscription cancelled successfully',
            category: 'subscription',
            severity: 'info'
          }
        ]);

        setAlerts([
          {
            id: 'alert_001',
            message: 'High error rate in subscription operations',
            severity: 'high',
            timestamp: new Date(Date.now() - 900000).toISOString(),
            resolved: false
          }
        ]);
      }
    } catch (error) {
      productionLogger.error('Failed to fetch monitoring data', { error: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchMonitoringData();
      
      // Refresh data every 30 seconds while open
      const interval = setInterval(fetchMonitoringData, 30000);
      return () => clearInterval(interval);
    }
  }, [open, fetchMonitoringData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <ErrorIcon />;
      case 'medium':
      case 'low':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <CheckIcon />;
    }
  };

  const getMetricStatus = (value, thresholds) => {
    if (value >= thresholds.critical) return 'error';
    if (value >= thresholds.warning) return 'warning';
    return 'success';
  };

  if (!metrics) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Production Monitoring</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography>Loading monitoring data...</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MonitorIcon />
        Production Monitoring
        <Chip 
          label="LIVE" 
          color="success" 
          size="small" 
          sx={{ ml: 'auto' }} 
        />
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Error Tracking" />
          <Tab label="Performance" />
          <Tab label="Security & Alerts" />
        </Tabs>

        {/* Overview Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* System Health */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">System Health</Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <NetworkIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6">Online</Typography>
                        <Typography variant="body2" color="text.secondary">API Status</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <SpeedIcon sx={{ 
                          fontSize: 40, 
                          color: getMetricStatus(metrics.performance.cacheEfficiency.hitRate, { critical: 90, warning: 70 }),
                          mb: 1 
                        }} />
                        <Typography variant="h6">{metrics.performance.cacheEfficiency.hitRate}%</Typography>
                        <Typography variant="body2" color="text.secondary">Cache Hit Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <ErrorIcon sx={{ 
                          fontSize: 40, 
                          color: getMetricStatus(
                            Object.values(metrics.frontend).reduce((a, b) => a + b, 0), 
                            { critical: 20, warning: 10 }
                          ),
                          mb: 1 
                        }} />
                        <Typography variant="h6">
                          {Object.values(metrics.frontend).reduce((a, b) => a + b, 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Errors</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <SecurityIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h6">{metrics.frontend.tokenRefreshFailures}</Typography>
                        <Typography variant="body2" color="text.secondary">Token Failures</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Error Breakdown */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ErrorIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Error Breakdown</Typography>
                    </Box>
                    <IconButton onClick={() => toggleSection('errors')}>
                      {expandedSections.errors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  
                  <Collapse in={expandedSections.errors}>
                    <List dense>
                      {Object.entries(metrics.frontend).map(([errorType, count]) => (
                        <ListItem key={errorType}>
                          <ListItemIcon>
                            <Chip 
                              label={count} 
                              color={count > 5 ? 'error' : count > 2 ? 'warning' : 'default'}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={errorType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            secondary={count > 10 ? 'Requires attention' : 'Normal'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Performance Metrics</Typography>
                    </Box>
                    <IconButton onClick={() => toggleSection('performance')}>
                      {expandedSections.performance ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  
                  <Collapse in={expandedSections.performance}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Session Duration: {(metrics.performance.sessionDuration / 1000 / 60).toFixed(1)} minutes
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((metrics.performance.sessionDuration / 1000 / 60 / 30) * 100, 100)} 
                        sx={{ mt: 1, mb: 2 }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary">
                      API Endpoints Tracked: {Object.keys(metrics.performance.apiCalls).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cache Entries: {metrics.cache.totalEntries} total, {metrics.cache.activeEntries} active
                    </Typography>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Error Tracking Tab */}
        {activeTab === 1 && (
          <Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Severity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label={log.level} size="small" />
                      </TableCell>
                      <TableCell>{log.message}</TableCell>
                      <TableCell>{log.category}</TableCell>
                      <TableCell>
                        <Chip 
                          label={log.severity} 
                          color={getSeverityColor(log.severity)}
                          size="small"
                          icon={getSeverityIcon(log.severity)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Performance Tab */}
        {activeTab === 2 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>API Performance</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Endpoint</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Avg Duration</TableCell>
                        <TableCell align="right">Cache Hit Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(metrics.performance.apiCalls).map(([endpoint, metric]) => (
                        <TableRow key={endpoint}>
                          <TableCell>{endpoint}</TableCell>
                          <TableCell align="right">{metric.count}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`${metric.averageDuration.toFixed(0)}ms`}
                              color={metric.averageDuration > 1000 ? 'error' : metric.averageDuration > 500 ? 'warning' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`${metric.cacheHitRate}%`}
                              color={metric.cacheHitRate > 70 ? 'success' : metric.cacheHitRate > 30 ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Security & Alerts Tab */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>Active Alerts</Typography>
            {alerts.length === 0 ? (
              <Alert severity="success">
                <Typography>No active alerts. System is operating normally.</Typography>
              </Alert>
            ) : (
              <List>
                {alerts.map((alert) => (
                  <ListItem key={alert.id}>
                    <ListItemIcon>
                      {getSeverityIcon(alert.severity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.message}
                      secondary={`${alert.severity.toUpperCase()} - ${new Date(alert.timestamp).toLocaleString()}`}
                    />
                    <Chip 
                      label={alert.resolved ? 'Resolved' : 'Active'}
                      color={alert.resolved ? 'success' : 'error'}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={fetchMonitoringData} 
          startIcon={<RefreshIcon />}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonitoringDashboard;